import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';

import {AuthGuard} from './core/auth.guard';
import {NgxPermissionsGuard} from 'ngx-permissions';

import {MainComponent} from './main/main.component';
import {AuthComponent} from './auth/auth.component';
import {LoginComponent} from './auth/login/login.component';
import {VersionComponent} from './auth/version/version.component';
import {DiscoverComponent} from './main/discover/discover.component';
import {ResetComponent} from './auth/reset/reset.component';
import {InfluencerGroupsComponent} from './main/influencer-groups/influencer-groups.component';
import {GroupDetailsComponent} from './main/group-details/group-details.component';
import {InfluencerComponent} from './main/influencer/influencer.component';
import {PostOverviewComponent} from './main/post-overview/post-overview.component';
import {CampaignsAddComponent} from './main/campaigns-add/campaign-add.component';
import {CampaignsComponent} from './main/campaigns/campaigns.component';
import {CampaignsEditComponent} from './main/campaigns-add/campaign-edit.component';
import {AdminComponent} from './main/admin/admin.component';
import {MeasureComponent} from './main/measure/measure.component';
import {LoginGuard} from './core/login.guard';
import {PdfViewerComponent} from './main/pdf-viewer/pdf-viewer.component';
import {InfluencerDetailPdfViewerComponent} from './main/pdf-viewer/influencer-detail-pdf-viewer/influencer-detail-pdf-viewer.component';
import {PostOverviewPdfViewerComponent} from './main/pdf-viewer/post-overview-pdf-viewer/post-overview-pdf-viewer.component';
import {MeasurePdfViewerComponent} from './main/pdf-viewer/measure-pdf-viewer/measure-pdf-viewer.component';
import {PdfGuard} from './core/pdf.guard';
import {PdfDeactivateGuard} from './core/pdfDeactivate.guard';
import {PlatformSettingsComponent} from './main/admin/platform-settings/platform-settings.component';
import {ActivePages, Page} from './services/event-tracker/event-tracker.service';
import {LevelsGuard} from './core/levels.guard';
import {SignUpComponent} from './auth/sign-up/sign-up.component';
import {VerifyNewAccountComponent} from './auth/verify-new-account/verify-new-account.component';
import {ConfirmPasswordComponent} from './auth/confirm-password/confirm-password.component';
import {CampaignNewComponent} from './main/campaign-new/campaign-new.component';
// pages for influencer platform
import {InfluencerDetailsComponent} from './influencer-platform/influencer-details/influencer-details.component';
import {InfluencerGuard} from './core/influencer.guard';
import {InfluencerAuthComponent} from './influencer-auth/influencer-auth.component';
// influencer main
import {InfluencerMainComponent} from './influencer-main/influencer-main.component';
import {InfluencerSignUpComponent} from './influencer-auth/influencer-sign-up/influencer-sign-up.component';
import {InfluencerLoginComponent} from './influencer-auth/influencer-login/influencer-login.component';
import {InfluencerCampaignDetailComponent} from './influencer-platform/influencer-campaign-detail/influencer-campaign-detail.component';
import {InfluencerInboxComponent} from './main/influencer-inbox/influencer-inbox.component';
import {InfluencerSettingsComponent} from './influencer-platform/influencer-settings/influencer-settings.component';
import {InfluencerHomeComponent} from './main/influencer-home/influencer-home.component';
import {CampaignPdfViewerComponent} from './main/pdf-viewer/campaign-pdf-viewer/campaign-pdf-viewer.component';
import {CampaignGuard} from './core/campaign.guard';
import {ContactPermissionComponent} from './influencer-platform/contact-permission/contact-permission.component';
import {InfluencerListComponent} from './main/influencer-list/influencer-list.component';
import { AccountSettingComponent } from './main/account-setting/account-setting.component';
import {NotFoundPageComponent} from './mmi/not-found-page/not-found-page.component';
import {InfluencerReviewComponent} from './main/admin/influencer-review/influencer-review.component';
import {EmailSettingsComponent} from './main/email-settings/email-settings.component';
import { AdvertiserAppAuthGuard } from './core/advertiser-app-auth.guard';
import { InactiveAccountComponent } from './auth/inactive-account/inactive-account.component';
import { NonSsoAuthGuard } from './core/non-sso-auth.guard';

import { FbLoginFailComponent } from './fb-testing-page/fb-login-fail/fb-login-fail.component';
import { FbAuthSuccessDeligatorComponent } from './core/fb-auth-success-deligator/fb-auth-success-deligator.component';
import { InfluencerConnectionComponent } from './influencer-platform/influencer-connection/influencer-connection.component';
import { InfluencerAllSetComponent } from './influencer-platform/influencer-all-set/influencer-all-set.component';
import { ForwardComponent } from './core/forward/forward.component';

const appRoutes: Routes = [
  {
    path: '',
    component: AuthComponent,
    children: [
      { path: 'reset-password', component: ResetComponent, canActivate: [LoginGuard] },
      { path: 'resetPassword/:hashKey', component: ConfirmPasswordComponent, canActivate: [LoginGuard] },
      { path: 'login', component: LoginComponent, canActivate: [LoginGuard] },
      { path: 'verify', component: VerifyNewAccountComponent },
      // default public route is login
      { path: '', component: ForwardComponent, pathMatch: 'full' }
    ]
  },
  {
    path: '404',
    component: NotFoundPageComponent
  },
  {
    path: 'version',
    component: VersionComponent
  },
  {
    path: 'invite/welcome/:hashKey',
    component: SignUpComponent,
    canActivate: [LoginGuard]
  },
  {
    path: 'fb-auth-error',
    component: FbLoginFailComponent,
  },
  {
    path: 'fb-auth-success',
    component: FbAuthSuccessDeligatorComponent,
  },
  { path: 'influencer/contact-permission',
    component: InfluencerConnectionComponent
  },
  { path: 'user/all-set',
    component: InfluencerAllSetComponent
  },
  { path: 'email-settings',
    component: EmailSettingsComponent
  },
  {
    path: '',
    component: InfluencerAuthComponent,
    children: [
      { path: 'influencer/sign-up', component: InfluencerSignUpComponent, canActivate: [LoginGuard] },
      { path: 'influencer/login', component: InfluencerLoginComponent, canActivate: [LoginGuard] },
    ]
  },
  {
    path: '',
    component: InfluencerMainComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'dashboard',
        canActivate: [InfluencerGuard, NgxPermissionsGuard],
        data: {
          permissions: {
            only: 'main_menu.enable_home', // We actually need new permission for Influencer but the server need to adjust to reponse permissions for Influencer, not only create new permission-rules in the code
            redirectTo: '/influencer/login'
          }
        },
        children: [
          {
            path: 'home',
            component: InfluencerHomeComponent
          },
          {
            path: 'details',
            children: [
              { path: 'overview', component: InfluencerDetailsComponent, data: {order: 1}},
              { path: 'insight', component: InfluencerDetailsComponent, data: {order: 2}},
              { path: 'demographics', component: InfluencerDetailsComponent, data: {order: 3}},
              { path: '', redirectTo: 'insight', pathMatch: 'full' }
            ],
            canActivate: [NgxPermissionsGuard],
            data: {
              permissions: {
                only: 'influencer_platform.enable_influencer_details',
                redirectTo: '/influencer/login'
              }
            }
          },
          {
            path: 'settings',
            component: InfluencerSettingsComponent
          },
          {
            path: 'inbox',
            component: InfluencerInboxComponent,
            canActivate: [NgxPermissionsGuard],
          },
          {
            path: 'campaigns/:campaignId/new',
            component: CampaignNewComponent,
            canActivate: [CampaignGuard, NgxPermissionsGuard],
            data: {
              permissions: {
                only: 'influencer_platform.enable_influencer_details',
                redirectTo: '/influencer/login'
              },
              needToConfirm: true
            }
          },
          {
            path: 'campaigns/:campaignId/active',
            component: InfluencerCampaignDetailComponent,
            canActivate: [CampaignGuard, NgxPermissionsGuard],
            data: {
              permissions: {
                only: 'influencer_platform.enable_influencer_details',
                redirectTo: '/influencer/login'
              }
            }
          },
          {
            path: 'campaigns',
            canActivate: [CampaignGuard, NgxPermissionsGuard],
            component: CampaignsComponent,
            data: {
              permissions: {
                only: 'influencer_platform.enable_influencer_campaigns',
                redirectTo: '/influencer/login'
              },
              onInfluencerPlatform: true,
            }
          },
          {
            path: 'post/:urlHash',
            component: PostOverviewComponent,
            canActivate: [NgxPermissionsGuard],
            data: {
              onInfluencerPlatform: true
            }
          },
          {
            path: 'contact-permission',
            component: ContactPermissionComponent,
            canActivate: [NgxPermissionsGuard],
            data: {
              onInfluencerPlatform: true
            }
          },
        ]
      },
      {
        path: 'pdf/campaign/:campaignInfluencerId',
        component: CampaignPdfViewerComponent,
        canActivate: [InfluencerGuard, PdfGuard, NgxPermissionsGuard],
        canDeactivate: [PdfDeactivateGuard],
        data: {
          permissions: {
            only: 'influencer_platform.enable_influencer_details',
            redirectTo: '/influencer/login'
          }
        }
      }
    ]
  },
  {
    path: '',
    component: MainComponent,
    canActivate: [AuthGuard],
    canActivateChild: [AdvertiserAppAuthGuard],
    children: [
      {
        path: 'discover',
        component: DiscoverComponent,
        canActivate: [NgxPermissionsGuard],
        data: {
          permissions: {
            only: 'main_menu.enable_discover',
            redirectTo: '/404'
          },
          // use for Segment Analytics and Mixpannel. Refer: https://segment.com/
          segmentItem: Page.DISCOVER_TAB,
          segmentActivePage: ActivePages.DISCOVER
        }
      },
      {
        path: 'discover/posts',
        component: DiscoverComponent,
        canActivate: [NgxPermissionsGuard],
        data: {
          activeTab: 'posts',
          permissions: {
            only: 'main_menu.enable_discover',
            redirectTo: '/404'
          },
          // use for Segment Analytics and Mixpannel. Refer: https://segment.com/
          segmentItem: Page.DISCOVER_TAB,
          segmentActivePage: ActivePages.DISCOVER
        }
      },
      {
        path: 'discover/influencers',
        component: DiscoverComponent,
        canActivate: [NgxPermissionsGuard],
        data: {
          activeTab: 'influencers',
          permissions: {
            only: 'main_menu.enable_discover',
            redirectTo: '/404'
          },
          // use for Segment Analytics and Mixpannel. Refer: https://segment.com/
          segmentItem: Page.DISCOVER_TAB,
          segmentActivePage: ActivePages.DISCOVER
        }
      },
      {
        path: 'groups',
        component: InfluencerGroupsComponent,
        canActivate: [NgxPermissionsGuard],
        data: {
          permissions: {
            only: 'main_menu.enable_discover',
            redirectTo: '/404'
          },
          // use for Segment Analytics and Mixpannel. Refer: https://segment.com/
          segmentItem: Page.INFLUENCER_TAB,
          segmentActivePage: ActivePages.GROUP_OVERVIEW
        }
      },
      {
        path: 'groups',
        children: [
          { path: 'monthly-visit',
            component: GroupDetailsComponent,
            data: {
              isRecentlyVisitedInfluencerGroup: true
            }
          },
          { path: ':influencer_group_id', component: GroupDetailsComponent}
        ],
        canActivate: [NgxPermissionsGuard],
        data: {
          permissions: {
            only: 'main_menu.enable_discover',
            redirectTo: '/404'
          }
        }
      },
      {
        path: 'influencer/:id',
        children: [
          { path: 'overview', component: InfluencerComponent, data: {order: 1}},
          { path: 'insight', component: InfluencerComponent, data: {order: 2}},
          { path: 'demographics', component: InfluencerComponent, data: {order: 3}},
          { path: 'activity', component: InfluencerComponent, data: {order: 4}},
          { path: 'campaign',
            canActivate: [LevelsGuard],
            component: InfluencerComponent,
            data: {
              order: 5,
              levels: ['Premium', 'Enterprise']
            }
          },
          { path: '', redirectTo: 'overview', pathMatch: 'full' }
        ],
        canActivate: [NgxPermissionsGuard],
        data: {
          permissions: {
            only: 'main_menu.enable_home',
            redirectTo: '/404'
          },
          // use for Segment Analytics and Mixpannel. Refer: https://segment.com/
          segmentItem: Page.INFLUENCER_TAB,
          segmentActivePage: ActivePages.INFLUENCER_DETAILS
        }
      },
      {
        path: 'post',
        component: PostOverviewComponent,
        canActivate: [NgxPermissionsGuard],
        data: {
          permissions: {
            only: 'main_menu.enable_home',
            redirectTo: '/404'
          }
        }
      },
      { path: 'framed/admin',
        component: AdminComponent,
        canActivate: [NonSsoAuthGuard, NgxPermissionsGuard],
        data: {
          permissions: {
            only: 'user_menu.enable_admin',
            redirectTo: '/login'
          }
        }
      },
      { path: 'account-settings',
        children: [
          { path: 'user-settings', component: AccountSettingComponent, data: {activeTab: 'user-settings'} },
          { path: 'billing',
            component: AccountSettingComponent,
            data: {
              activeTab: 'billing',
              segmentItem: Page.INFLUENCER_TAB,
              segmentActivePage: ActivePages.INFLUENCER_DETAILS
            }
          },
          { path: '', redirectTo: 'user-settings', pathMatch: 'full' }
        ],
        canActivate: [NonSsoAuthGuard, NgxPermissionsGuard],
        data: {
          permissions: {
            only: 'main_menu.enable_home',
            redirectTo: '/login'
          }
        }
      },
      {
        path: 'campaigns',
        canActivate: [NgxPermissionsGuard, LevelsGuard],
        children: [
          {
            path: 'history',
            component: CampaignsComponent,
            data: {
              segmentActivePage: ActivePages.CAMPAIGN_HISTORY,
              isCampaignHistory: true
            }
          },
          {
            path: '',
            component: CampaignsComponent,
            pathMatch: 'full',
            data: {
              segmentActivePage: ActivePages.CAMPAIGN_OVERVIEW
            }
          }
        ],
        data: {
          permissions: {
            only: 'main_menu.enable_campaigns',
            redirectTo: '/404'
          },
          levels: ['Free', 'Basic', 'Premium', 'Enterprise'],
          // use for Segment Analytics and Mixpannel. Refer: https://segment.com/
          segmentItem: Page.CAMPAIGN_TAB,
          segmentActivePage: ActivePages.CAMPAIGN_OVERVIEW
        }
      },
      {
        path: 'campaigns-add',
        canActivate: [NgxPermissionsGuard, LevelsGuard],
        component: CampaignsAddComponent,
        data: {
          permissions: {
            only: 'main_menu.enable_campaigns',
            redirectTo: '/404'
          },
          levels: ['Premium', 'Enterprise']
        }
      },
      {
        path: 'campaigns-add/:tab-key',
        canActivate: [NgxPermissionsGuard, LevelsGuard],
        component: CampaignsAddComponent,
        data: {
          permissions: {
            only: 'main_menu.enable_campaigns',
            redirectTo: '/404'
          },
          levels: ['Premium', 'Enterprise']
        }
      },
      {
        path: 'campaigns/:id',
        canActivate: [NgxPermissionsGuard, LevelsGuard],
        children: [
          {
            path: 'influencer-list',
            component: InfluencerListComponent,
          },
          {
            path: '',
            component: CampaignsEditComponent,
          },
        ],
        data: {
          permissions: {
            only: 'main_menu.enable_campaigns',
            redirectTo: '/404'
          },
          levels: ['Free', 'Basic', 'Premium', 'Enterprise']
        }
      },
      {
        path: 'campaigns/:id/:tab-key',
        canActivate: [NgxPermissionsGuard, LevelsGuard],
        component: CampaignsEditComponent,
        data: {
          permissions: {
            only: 'main_menu.enable_campaigns',
            redirectTo: '/404'
          },
          levels: ['Free', 'Basic', 'Premium', 'Enterprise']
        }
      },
      {
        path: 'measure',
        canActivate: [NgxPermissionsGuard],
        component: MeasureComponent,
        data: {
          permissions: {
            only: 'main_menu.enable_measure',
            redirectTo: '/404'
          },
          // use for Segment Analytics and Mixpannel. Refer: https://segment.com/
          segmentItem: Page.MEASURE_TAB,
          segmentActivePage: ActivePages.MEASURE
        }
      },
      {
        path: 'measure/:campaign_id',
        canActivate: [NgxPermissionsGuard],
        component: MeasureComponent,
        data: {
          permissions: {
            only: 'main_menu.enable_measure',
            redirectTo: '/404'
          },
          // use for Segment Analytics and Mixpannel. Refer: https://segment.com/
          segmentItem: Page.MEASURE_TAB,
          segmentActivePage: ActivePages.MEASURE
        }
      },
      {
        path: 'pdf',
        component: PdfViewerComponent,
        canActivate: [PdfGuard, NgxPermissionsGuard],
        canDeactivate: [PdfDeactivateGuard],
        children: [
          { path: 'influencer/:id', component: InfluencerDetailPdfViewerComponent},
          { path: 'post', component: PostOverviewPdfViewerComponent},
          { path: 'measure', component: MeasurePdfViewerComponent}
        ],
        data: {
          permissions: {
            only: 'main_menu.enable_home',
            redirectTo: '/404'
          }
        },
      },
      {
        path: 'admin/platform-settings',
        component: PlatformSettingsComponent,
        canActivate: [NonSsoAuthGuard],
        data: {
        }
      },
      {
        path: 'admin/influencer-review',
        component: InfluencerReviewComponent,
        canActivate: [NonSsoAuthGuard, NgxPermissionsGuard],
        data: {
          permissions: {
            only: 'user_menu.enable_admin',
            redirectTo: '/login'
          }
        }
      },
      {
        path: 'inbox',
        component: InfluencerInboxComponent,
        canActivate: [NgxPermissionsGuard, LevelsGuard],
        data: {
          permissions: {
            only: 'main_menu.enable_inbox',
            redirectTo: '/404'
          },
          levels: ['Premium', 'Enterprise'],
          // use for Segment Analytics and Mixpannel. Refer: https://segment.com/
          segmentItem: Page.INBOX_TAB,
          segmentActivePage: ActivePages.INBOX
        }
      },
      {
        path: 'inactive-account',
        component: InactiveAccountComponent,
      },
      { path: '', redirectTo: '/discover', pathMatch: 'full' }
    ]
  },
  { path: '', redirectTo: '/discover', pathMatch: 'full' },
  // otherwise redirect to home
  {path: '**', redirectTo: ''}
];

@NgModule({
  imports: [
    RouterModule.forRoot(appRoutes, {
      enableTracing: false,
      useHash: true
    })
  ],
  exports: [RouterModule],
  providers: []
})
export class AppRouteModule { }



// WEBPACK FOOTER //
// ./src/app/app.route.module.ts